import { authoringReaders, latestAuthoringRequest, PART_SLOTS, pickerMembers, quoteEnchantment, remixSelections,
    templateCommand, type AuthoringFacts, type PartSlot, type Row, type Selections } from '../janus-engine/authoring/enchantment';
import { buildTrap, trapCommand, trapObjectFacts, trapObjectOptions, trapRecommendation, type TrapInput } from '../janus-engine/authoring/trap';
import { trapPanelOptions } from '../janus-engine/authoring/trap-panel';
import { readInventoryField, readInventoryRows } from '../janus-engine/compile/inventory-raw-readers';
import type {} from '../shared/enchantment-validation-client';
import type { InventorySharedCatalog } from '../janus-engine/compile/inventory-shared-catalog';

type Control = HTMLInputElement | HTMLSelectElement;
interface TomSelect {
    getValue(): string | string[]; clear(silent?: boolean): void; clearOptions(): void;
    addOptions(rows: { value: string; text: string }[]): void; setValue(values: string | string[], silent?: boolean): void;
    refreshOptions(trigger?: boolean): void;
}
type Select = HTMLSelectElement & { tomselect?: TomSelect };
const control = (id: string) => document.getElementById(id) as Control | null;
const value = (id: string) => control(id)?.value ?? '';
const values = (id: string): string[] => {
    const select = document.getElementById(id) as Select | null;
    const selected = select?.tomselect?.getValue();
    return Array.isArray(selected) ? selected.filter(Boolean) : typeof selected === 'string' ? selected ? [selected] : []
        : Array.from(select?.selectedOptions ?? [], option => option.value).filter(Boolean);
};
const checked = (id: string) => (control(id) as HTMLInputElement | null)?.checked ?? false;
const text = (id: string, content: unknown) => { const node = document.getElementById(id); if (node) node.textContent = String(content ?? ''); };
const setValue = (id: string, content: string) => { const input = control(id); if (input) input.value = content; };
const endpoint = (query: Record<string, string>) => `${BASE_URL}options/api/enchantment_authoring_inputs_inc.php?${new URLSearchParams(query)}`;
const format = (amount: number) => amount.toLocaleString('en-US', { maximumFractionDigits: 2 });
async function readJson(url: string): Promise<Row> {
    try {
        const response = await fetch(url, { credentials: 'same-origin' });
        const body = await response.json() as Row;
        if (!response.ok || body.error) throw new Error(String(body.error ?? `HTTP ${response.status}`));
        return body;
    } catch (error) {
        const reason = error instanceof Error ? error.message : String(error);
        throw new Error(reason.startsWith('unported:') ? reason : `unported:enchantment-authoring:read-failed:${reason}`);
    }
}
function options(id: string, rows: { value: string; text: string }[], selected: string[]): void {
    const select = document.getElementById(id) as Select | null;
    if (!select) return;
    if (select.tomselect) {
        select.tomselect.clear(true); select.tomselect.clearOptions(); select.tomselect.addOptions(rows);
        select.tomselect.setValue(select.multiple ? selected : selected[0] ?? '', true); select.tomselect.refreshOptions(false);
    } else {
        select.replaceChildren(...rows.map(row => new Option(row.text, row.value, false, selected.includes(row.value))));
    }
}
function errorMessage(error: unknown, id: string): void {
    const message = error instanceof Error ? error.message : String(error);
    text(id, `${message} — reload the authoring inputs to retry.`);
    if (message.startsWith('unported:')) window.dispatchEvent(new CustomEvent('ft:unported', { detail: { reason: message, slice: 'C6-LIVE-ENCHANTMENT-S5-1020' } }));
}
function resultPanel(id: string, result: Row, name: string): void {
    const host = document.getElementById(id); if (!host) return;
    host.replaceChildren();
    host.dataset.ftAuthoringSaved = String(result.template_id);
    const heading = document.createElement('h5'); heading.textContent = result.reused ? 'This template already exists:' : 'Created New Enchantment Template:';
    host.append(heading);
    for (const [label, entity, persistedName] of [['Template', result.template_id, result.template_name], ['Trap ability', result.ability_id, result.ability_name]]) {
        if (!entity) continue;
        const line = document.createElement('div'), link = document.createElement('a');
        link.href = `${BASE_URL}item.php?id=${encodeURIComponent(String(entity))}`; link.target = '_blank';
        link.textContent = `${label}: ${persistedName || name || entity}`; line.append(link); host.append(line);
    }
    const copy = document.createElement('button'); copy.type = 'button'; copy.className = 'btn btn-sm btn-primary'; copy.textContent = 'Copy template ID';
    copy.addEventListener('click', () => { void navigator.clipboard.writeText(String(result.template_id)).then(() => { copy.textContent = 'Copied!'; }); });
    host.append(copy);
}
async function saveCommand(command: Row, kind: 'template' | 'trap'): Promise<Row> {
    const path = kind === 'template' ? 'create_enchantment_template/create_enchantment_template_inc.php' : 'create_trap/create_trap_inc.php';
    const response = await fetch(`${BASE_URL}options/tools/${path}`, { method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(command) });
    const result = await response.json() as Row;
    if (!response.ok || result.ok !== true) throw new Error(String(result.error ?? 'Authoring save failed'));
    return result;
}

/** Compile/adopt through the page's single C5 worker and cache. The page only holds its current facts. */
async function selectedEntity(id: string): Promise<{ row: Row; raw: Row; definitions: Record<string, Row> }> {
    const host = await readJson(endpoint({ selected: id }));
    const envelope = (host._entity_base ?? host._inventory_base) as { base: Row };
    if (!envelope?.base) throw new Error('unported:enchantment-authoring:missing-base');
    const raw = envelope.base;
    if (!window.ftCompileFetchedEntity) throw new Error('unported:enchantment-authoring:compile-bridge');
    const shared = host.entity_catalog as InventorySharedCatalog | undefined;
    const adopted = await window.ftCompileFetchedEntity(host, shared);
    const row = host._inventory_base ? readInventoryRows(adopted, 'inventory')
        .find(node => String(readInventoryField(node, 'inventory_id')).toUpperCase() === id.toUpperCase()) : adopted;
    if (!row) throw new Error('unported:enchantment-authoring:missing-inventory-root');
    return { row, raw, definitions: { ...host.selected_definitions as Record<string, Row> | undefined,
        ...shared?.base.catalog as Record<string, Row> | undefined,
        ...raw.catalog as Record<string, Row> | undefined } };
}

function wireTemplate(): void {
    const button = document.getElementById('btn_submit_enchantment_template') as HTMLButtonElement | null;
    if (!button) return;
    const submit: HTMLButtonElement = button;
    const gate = latestAuthoringRequest();
    let factsPromise: Promise<AuthoringFacts> | undefined;
    let pendingRemix = value('from_enchantment_template_id'), ready: { base: Row; quote: ReturnType<typeof quoteEnchantment>; facts: AuthoringFacts } | undefined;
    let saveIdentity = '', saveFingerprint = '', saving = false;
    const load = () => factsPromise ??= readJson(endpoint({ kind: 'template', remix: pendingRemix }))
        .then(row => {
            if (row.schema !== 'enchantment-authoring-inputs.v1') throw new Error('unported:enchantment-authoring:input-shape');
            const user = (window as unknown as { FateTide?: { user?: { id?: string; primaryRole?: string } } }).FateTide?.user;
            return { ...row, viewer: { id: user?.id ?? '', staff: ['employee', 'admin', 'super_admin'].includes(user?.primaryRole ?? '') } } as unknown as AuthoringFacts;
        }).catch(error => { factsPromise = undefined; throw error; });
    const collect = (): Selections => Object.fromEntries([...PART_SLOTS.map(slot => [slot, values(slot)]),
        ['item_charges', [value('item_charges')]], ['item_rechargeable', [value('item_rechargeable')]]]);
    async function refresh(): Promise<void> {
        const generation = gate.begin(); ready = undefined; submit.disabled = true;
        delete document.getElementById('div_refresh_runeforging_passive_abilities')!.dataset.ftAuthoringReady;
        text('ft_enchantment_authoring_message', 'Loading authoring inputs…');
        try {
            const loaded = await load(); if (!gate.current(generation)) return;
            const baseId = value('base_object');
            if (!baseId) { text('ft_enchantment_authoring_message', 'Choose a base object.'); return; }
            const selected = collect(), base = await selectedEntity(baseId);
            if (!gate.current(generation)) return;
            const rawFacts = { ...loaded, definitions: { ...loaded.definitions, ...base.definitions } };
            const baseRead = authoringReaders(rawFacts), baseItemId = String(baseRead.itemField(base.row, 'item_id') ?? '');
            if (baseItemId && !rawFacts.definitions[baseItemId.toUpperCase()] && base.raw.items) rawFacts.definitions[baseItemId.toUpperCase()] = base.raw;
            if (pendingRemix) {
                const source = loaded.templates.find(row => String(row.enchantment_template_id).toUpperCase() === pendingRemix.toUpperCase());
                if (!source || Number(source.enchantment_template_deleted ?? 0)) throw new Error('unported:enchantment-authoring:missing-remix');
                if (Number(source.enchantment_template_private ?? 0) && !loaded.viewer?.staff
                    && String(source.enchantment_template_created_by_id ?? '').toUpperCase() !== loaded.viewer?.id.toUpperCase()) throw new Error('This remix is unavailable in your catalog.');
                const remix = remixSelections(loaded.members, rawFacts);
                for (const slot of [...PART_SLOTS, 'item_charges', 'item_rechargeable'] as const) selected[slot] = remix.selections[slot] ?? [];
                // Absence means non-rechargeable on an existing charged template.
                if (selected.charged_spell?.length || selected.charged_ability?.length) selected.item_rechargeable = [remix.selections.item_rechargeable?.[0] ?? '0'];
                text('ft_enchantment_remix_warnings', remix.warnings.join('\n'));
            }
            const membership = pickerMembers(base.row, rawFacts), read = authoringReaders(rawFacts);
            const selectedParts = [...new Set(PART_SLOTS.flatMap(slot => selected[slot] ?? []))];
            const compiled = await Promise.all(selectedParts.map(async id => [id.toUpperCase(), (await selectedEntity(id)).row] as const));
            if (!gate.current(generation)) return;
            const quotedFacts = { ...rawFacts, definitions: { ...rawFacts.definitions, ...Object.fromEntries(compiled) } };
            const quote = quoteEnchantment(base.row, selected, quotedFacts);
            // Apply only after every read/compile succeeds. Failed and overtaken requests leave no stale quote armed.
            for (const slot of PART_SLOTS) {
                const own = selected[slot] ?? [];
                const peers = slot.includes('spell') ? PART_SLOTS.filter(key => key.includes('spell') && key !== slot) : [];
                const used = new Set(peers.flatMap(key => selected[key] ?? []));
                const ids = [...new Set([...membership[slot].filter(id => !used.has(id)), ...own])];
                options(slot, ids.map(id => ({ value: id, text: read.name(read.definition(id)) || id })), own);
            }
            setValue('item_charges', quote.charges ? String(quote.charges) : '');
            if (quote.charges) setValue('item_rechargeable', quote.rechargeable ? '1' : '0');
            for (const slot of PART_SLOTS) {
                text(`div_refresh_${slot}_cost_gp`, format(quote.costs[slot]!));
                text(`div_refresh_${slot}_enchantment_cost_modifer`, quote.multipliers[slot]);
                text(`div_refresh_${slot}_subtotal_enchantment_cost`, format(quote.subtotals[slot]!));
            }
            text('div_refresh_base_object_item_type', read.itemField(base.row, 'item_type'));
            const equipment = read.id(read.layerValue(base.row, 'equipment_slot'));
            text('div_refresh_base_object_equipment_slot', read.name(read.definition(equipment)) || equipment || 'Off hand');
            text('div_refresh_base_object_cost_gp', quote.baseCost ? format(quote.baseCost) : 'Priceless');
            text('div_refresh_base_object_total_cost', format(quote.baseCost));
            text('div_refresh_cost_multiplier', quote.multiplier);
            text('div_refresh_item_charges_enchantment_cost_modifer', quote.chargeMultiplier);
            text('div_refresh_item_charges_subtotal_enchantment_cost', format(quote.subtotals.item_charges!));
            text('btn_highest_charged_spell', quote.required);
            text('div_refresh_overcharge_type', !quote.rechargeable || quote.charges < 10 ? 'None' : quote.charges >= 20 ? 'Major Overcharge' : 'Minor Overcharge');
            text('div_refresh_imbued_spells_total_cost', format(quote.subtotals.innate_spell_per_long_rest! + quote.subtotals.at_will_spell!));
            text('div_refresh_charged_spells_total_cost', format(quote.subtotals.item_charges! + quote.subtotals.charged_spell! + quote.subtotals.charged_ability!));
            text('div_refresh_passive_abilities_total_cost', format(['passive_ability', 'attack_ability', 'opportunity_attack_ability', 'trap_ability'].reduce((sum, slot) => sum + quote.subtotals[slot]!, 0)));
            text('div_refresh_enchantment_template_total_cost', format(quote.total));
            pendingRemix = ''; setValue('from_enchantment_template_id', '');
            ready = { base: base.row, quote, facts: rawFacts };
            submit.disabled = saving;
            document.getElementById('div_btn_submit_enchantment_template')!.style.display = 'block';
            text('ft_enchantment_authoring_message', '');
            document.getElementById('div_refresh_runeforging_passive_abilities')!.dataset.ftAuthoringReady = baseId;
        } catch (error) { if (gate.current(generation)) errorMessage(error, 'ft_enchantment_authoring_message'); }
    }
    document.addEventListener('change', event => {
        const id = (event.target as HTMLElement | null)?.id ?? '';
        if (id === 'base_object' || [...PART_SLOTS, 'item_charges', 'item_rechargeable'].includes(id as PartSlot)) {
            if (id === 'base_object' || PART_SLOTS.includes(id as PartSlot)) {
                const selected = values(id), infoId = selected[selected.length - 1]; if (infoId) void openItemInfo(infoId);
            }
            void refresh();
        }
    });
    document.addEventListener('click', event => {
        const trigger = (event.target as Element | null)?.closest<HTMLElement>('[data-ft-enchantment-action]'); if (!trigger) return;
        const action = trigger.dataset.ftEnchantmentAction;
        if (action === 'toggle-info') toggleEl(trigger.dataset.ftTarget ?? '');
        if (action === 'open-item-info') void openItemInfo(trigger.dataset.ftItemId ?? '');
        if (action === 'retry') { factsPromise = undefined; void refresh(); }
        if (action !== 'submit' || !ready || saving) return;
        const state = ready;
        void (async () => {
            saving = true; submit.disabled = true;
            try {
                const read = authoringReaders(state.facts), baseId = String(read.itemField(state.base, 'item_id'));
                const members = Object.entries(state.quote.selections).flatMap(([type, ids]) => ids!.map(id => ({ enchantment_type: type, enchantment_item_id: id })));
                if (!window.ftValidateEnchantment) throw new Error('unported:enchantment-authoring:validation-bridge');
                const verdict = window.ftValidateEnchantment(baseId, { members, definitions: state.facts.definitions, catalogRows: state.facts.metadata.layerCatalog });
                if (verdict !== true) throw new Error(verdict);
                const fingerprint = JSON.stringify(state.quote.selections);
                if (fingerprint !== saveFingerprint) { saveIdentity = crypto.randomUUID().toUpperCase(); saveFingerprint = fingerprint; }
                const receipt = await saveCommand(templateCommand(saveIdentity, state.quote), 'template');
                resultPanel('div_refresh_enchantment_template', receipt, state.quote.name);
            } catch (error) { errorMessage(error, 'div_refresh_enchantment_template'); }
            finally { saving = false; submit.disabled = !ready; }
        })();
    });
    async function boot() {
        const generation = gate.begin();
        try {
            if (pendingRemix && !value('base_object')) {
                const loaded = await load(), read = authoringReaders(loaded);
                if (!gate.current(generation) || value('base_object')) return;
                let hint = (loaded as AuthoringFacts & { hosts?: Row[] }).hosts?.[0];
                if (!hint) {
                    const counts = new Map<string, number>();
                    for (const member of loaded.members) for (const type of read.entries(read.definition(String(member.enchantment_item_id)), 'enchantment_item_type')) {
                        if (typeof type === 'string') counts.set(type, (counts.get(type) ?? 0) + 1);
                    }
                    const type = [...counts].sort((a, b) => b[1] - a[1])[0]?.[0];
                    if (type) {
                        const rows = (await readJson(endpoint({ base_type: type }))).base_candidates as Row[];
                        hint = rows.find(row => !Number(row.item_private ?? 0) && !Number(row.item_archived ?? 0));
                    }
                }
                if (!gate.current(generation) || value('base_object')) return;
                if (hint) options('base_object', [{ value: String(hint.item_id), text: String(hint.item_name) }], [String(hint.item_id)]);
            }
            if (!gate.current(generation)) return;
            await refresh();
        } catch (error) { errorMessage(error, 'ft_enchantment_authoring_message'); }
    }
    submit.disabled = true; void boot();
}

function wireTrap(): void {
    if (!document.getElementById('btn_submit_trap')) return;
    let factsPromise: Promise<AuthoringFacts> | undefined;
    const load = () => factsPromise ??= readJson(endpoint({ kind: 'trap' })).then(row => row as unknown as AuthoringFacts)
        .catch(error => { factsPromise = undefined; throw error; });
    const gate = latestAuthoringRequest();
    let fingerprint = '', templateId = '', abilityId = '';
    Object.assign(window, {
        async ftTrapAuthoringObject(id: string) {
            const facts = await load(); return trapObjectFacts(id, facts);
        },
        ftTrapAuthoringRecommendation: trapRecommendation,
        async ftTrapAuthoringSave(input: TrapInput) {
            const facts = await load(), build = buildTrap(input, facts), key = JSON.stringify(build);
            if (fingerprint !== key) { fingerprint = key; templateId = crypto.randomUUID().toUpperCase(); abilityId = crypto.randomUUID().toUpperCase(); }
            const receipt = await saveCommand(trapCommand(templateId, abilityId, build, facts), 'trap');
            resultPanel('div_refresh_trap_result', receipt, build.name);
            const guidance = document.createElement('p'); guidance.textContent = `Set the host's Inspection DC to ${build.dc} (recommended ${build.recommendation.dc}). Apply the template to a host object, then arm it.`;
            document.getElementById('div_refresh_trap_result')?.append(guidance);
            factsPromise = undefined; // A subsequent craft must see the just-saved donor for deduplication.
            return receipt;
        },
    });
    async function boot() {
        const generation = gate.begin();
        const button = document.getElementById('btn_submit_trap') as HTMLButtonElement; button.disabled = true;
        try {
            const facts = await load(); if (!gate.current(generation)) return;
            const selected = values('trap');
            options('trap', [{ value: '', text: '— Choose a trap —' }, ...trapObjectOptions(facts).map(row => ({ value: row.id, text: row.name }))], selected);
            text('div_refresh_trap_result', ''); button.disabled = !selected.length;
            window.dispatchEvent(new CustomEvent('ft:trap-authoring-ready'));
            if (selected.length) control('trap')?.dispatchEvent(new Event('change', { bubbles: true }));
        } catch (error) { if (gate.current(generation)) errorMessage(error, 'div_refresh_trap_result'); }
    }
    document.getElementById('ft_trap_authoring_retry')?.addEventListener('click', () => { factsPromise = undefined; void boot(); });
    void boot();
}

function wireTrapPanels(): void {
    const boot = (panel: HTMLElement): void => {
        if (panel.dataset.ftTrapPanelReady) return;
        const inputs = panel.querySelector('[data-ft-trap-panel-inputs]');
        if (!inputs || !panel.querySelector('#btn_place_encounter_trap')) return;
        panel.dataset.ftTrapPanelReady = 'loading';
        try {
            const facts = JSON.parse(inputs.textContent ?? '') as AuthoringFacts;
            const choices = trapPanelOptions(facts);
            const selected = values('encounter_trap_template');
            options('encounter_trap_template', [{ value: '', text: '--SELECT TRAP--' },
                ...choices.templates.map(row => ({ value: row.id, text: row.text }))], selected);
            const hosts = values('encounter_trap_host');
            options('encounter_trap_host', choices.hosts.map(row => ({ value: row.id, text: row.name })),
                hosts.length ? hosts : choices.hosts.slice(0, 1).map(row => row.id));
            control('encounter_trap_template')?.addEventListener('change', () => {
                const selected = choices.templates.find(row => row.id === value('encounter_trap_template'));
                if (selected) setValue('encounter_trap_dc', String(selected.dc));
            });
            text('div_refresh_encounter_trap_result', choices.templates.length ? ''
                : 'No traps crafted yet — build one with the Craft Trap tool, then place it here.');
            const button = panel.querySelector<HTMLButtonElement>('#btn_place_encounter_trap')!;
            button.disabled = !choices.templates.length || !choices.hosts.length;
            panel.dataset.ftTrapPanelReady = 'ready';
        } catch (error) {
            text('div_refresh_encounter_trap_result', error instanceof Error ? error.message : String(error));
            panel.dataset.ftTrapPanelReady = 'error';
        }
    };
    const scan = () => document.querySelectorAll<HTMLElement>('[data-ft-trap-panel]').forEach(boot);
    scan();
    // The encounter tools shell can arrive after the page's authoring bundle has booted.
    new MutationObserver(records => {
        if (records.some(record => Array.from(record.addedNodes).some(node => node instanceof Element
            && (node.matches('[data-ft-trap-panel]') || node.closest('[data-ft-trap-panel]') || node.querySelector('[data-ft-trap-panel]'))))) scan();
    }).observe(document.body, { childList: true, subtree: true });
}

export function wireAuthoring(): void {
    const boot = () => { wireTemplate(); wireTrap(); wireTrapPanels(); };
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
    else boot();
}

declare global {
    interface Window {
        ftTrapAuthoringObject?: (id: string) => Promise<ReturnType<typeof trapObjectFacts>>;
        ftTrapAuthoringRecommendation?: typeof trapRecommendation;
        ftTrapAuthoringSave?: (input: TrapInput) => Promise<Row>;
    }
}
